import { captureException } from "@sentry/vue";
import { LocalNotifications, type LocalNotificationSchema } from "@capacitor/local-notifications";

import type { PushNotificationOptions } from "../interfaces";
import { type PushNotificationStore, usePushNotificationsStore } from "../Store";

import useAppStore, { type AppStore } from "@/Modules/App/Store";

import { danger } from "@/Utils/Debug";
import { isValidString } from "@/Utils/String";
import { unixUTC } from "@/Utils/Date";
import { isApp } from "@/Utils/Ionic";

class ShowNotificationActionHandler {
  constructor(
    private readonly pushStore: PushNotificationStore = usePushNotificationsStore(),
    private readonly appStore: AppStore = useAppStore()
  ) {}

  public async handle(options: PushNotificationOptions): Promise<void> {
    try {
      if (await this.hasPermissions()) {
        const notification = {
          id: unixUTC(),
          schedule: { allowWhileIdle: true },
        } as LocalNotificationSchema;

        if (isValidString(options.title)) notification.title = options.title;
        if (isValidString(options.body)) notification.body = options.body;

        LocalNotifications.schedule({
          notifications: [notification],
        });
      }
    } catch (e) {
      danger("Error Show push notification");
      captureException(e);
    }
  }

  private async hasPermissions(): Promise<boolean> {
    if (!isApp()) return false;
    if (this.appStore.isForeground) return false;

    if (await this.pushStore.hasPermisions()) {
      return LocalNotifications.checkPermissions().then((status) => {
        if (status.display === "granted") {
          return true;
        }

        return LocalNotifications.requestPermissions().then((permStatus) => {
          if (permStatus.display === "granted") return true;

          return false;
        });
      });
    } else {
      return false;
    }
  }
}

export default ShowNotificationActionHandler;
